import React, { memo, useContext, useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { formattedDeadlineDate } from "../utils/dateHelpers";
import { useUpdateStudentMutation } from "../../services/admin/adminStudents";
import { NotificationContext } from "../../context/NotificationContext";

function getOverrideStatus(dateTime) {
  const date = new Date(dateTime);
  if (date > new Date()) {
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 800, color: "error.main", pl: 1 }}
        >
          Submission Override Until:
        </Box>
        <Box component="span" sx={{ color: "error.main", pl: 1 }}>
          {date.toLocaleString()}
        </Box>
      </Box>
    );
  }
  return null;
}

function ApplicationsHeader({
  user,
  isAdminView = false,
  onUserUpdate,
  generalClose,
  dasClose,
  noticeMessage,
}) {
  const { addNotification } = useContext(NotificationContext);
  const [updateStudent, { isLoading: isSavingComments }] =
    useUpdateStudentMutation();
  const adminComments =
    user?.admin_comments ??
    user?.adminComments ??
    user?.student_comments ??
    "";
  const [isEditingComments, setIsEditingComments] = useState(false);
  const [commentDraft, setCommentDraft] = useState(adminComments);

  useEffect(() => {
    setCommentDraft(adminComments);
  }, [adminComments]);

  let applicationName = "";
  let generalStatus = "";
  let dasStatus = "";
  let generalStatusOverride = "";
  let dasStatusOverride = "";

  if (user?.generalApp && user?.dasApp) {
    applicationName = "General & District/Authority Applications";
    generalStatus = user?.application_status || "In-Progress";
    generalStatusOverride = getOverrideStatus(user?.access_override_until);
    dasStatus = user?.das_application_status || "In-Progress";
    dasStatusOverride = getOverrideStatus(user?.dasAccessOverrideUntil);
  } else if (user?.generalApp) {
    applicationName = "General Application";
    generalStatus = user?.application_status || "In-Progress";
    generalStatusOverride = getOverrideStatus(user?.access_override_until);
  } else if (user?.dasApp) {
    applicationName = "District/Authority Application";
    dasStatus = user?.das_application_status || "In-Progress";
    dasStatusOverride = getOverrideStatus(user?.dasAccessOverrideUntil);
  }

  const handleSaveComments = async () => {
    if (!user?.id) return;

    try {
      await updateStudent({
        id: user.id,
        admin_comments: commentDraft,
      }).unwrap();
      onUserUpdate?.({ admin_comments: commentDraft });
      addNotification("Admin comments updated successfully.", "success");
      setIsEditingComments(false);
    } catch (error) {
      console.error("Failed to update admin comments:", error);
      addNotification(
        error?.data?.message || "Failed to update admin comments.",
        "error",
      );
    }
  };

  return (
    <Box className="application-header">
      {noticeMessage?.active && noticeMessage?.settingdate && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {noticeMessage?.value}
        </Alert>
      )}

      {/* Deadline Banner */}
      <Paper
        elevation={0}
        className="application-header-title"
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "#FFF8E5",
          width: "100%",
          mb: 2,
        }}
      >
        <Stack spacing={1}>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            textAlign="center"
            sx={{ lineHeight: 1.3 }}
          >
            Application Deadline
            {!(user?.dasApp && user?.generalApp) ? " is" : "s are"} at:
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="center"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            {(!user || user?.generalApp === true) && (
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "#FFE6E6",
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  className="application-header-deadlines"
                  sx={{ flexWrap: "wrap" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {(!user || user?.generalApp === true) && "General:"}
                  </Typography>
                  <Typography variant="body2" className="deadline-date">
                    {formattedDeadlineDate(generalClose)}
                  </Typography>
                </Stack>
              </Paper>
            )}

            {(!user || user?.dasApp === true) && (
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "#FFE6E6",
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  className="application-header-deadlines"
                  sx={{ flexWrap: "wrap" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {(!user || user?.dasApp === true) && "District/Authority:"}
                  </Typography>
                  <Typography variant="body2" className="deadline-date">
                    {formattedDeadlineDate(dasClose)}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
            <Chip
              label="**LATE APPLICATIONS WILL NOT BE CONSIDERED**"
              variant="outlined"
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* Title */}
      {user && (
        <Paper
          elevation={0}
          className="application-header-title-and-buttons"
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            className="application-header-header-title-text"
          >
            {applicationName}
          </Typography>
        </Paper>
      )}

      {/* Status Block */}
      {user && (
        <Paper
          elevation={0}
          className="application-header-app-status"
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box
            className="application-header-app-status-block"
            sx={{
              width: "100%",
              display: "block",
              p: 0,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box
                className="application-header-app-status-block-row-item"
                sx={{ minWidth: 0, display: "block", p: 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800 }}
                  className="application-header-app-status-block-row-item-bold-text"
                >
                  Application ID:
                </Typography>
                <Typography
                  variant="body2"
                  className="application-header-app-status-block-row-item-reg-text"
                >
                  {user?.id || "No ID"}
                </Typography>
              </Box>

              <Box
                className="application-header-app-status-block-row-item"
                sx={{ minWidth: 0, display: "block", p: 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800 }}
                  className="application-header-app-status-block-row-item-bold-text"
                >
                  Applicant Name:
                </Typography>
                <Typography
                  variant="body2"
                  className="application-header-app-status-block-row-item-reg-text"
                >
                  {`${user?.first_name} ${user?.last_name}${
                    user?.city ? " (" + user?.city + ")" : ""
                  }` || "No Name (No City)"}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {user?.generalApp && (
                <Box
                  className="application-header-app-status-block-row-item"
                  sx={{ minWidth: 0, display: "block", p: 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800 }}
                    className="application-header-app-status-block-row-item-bold-text"
                  >
                    General Application Status:
                  </Typography>
                  <Typography
                    variant="body2"
                    className="application-header-app-status-block-row-item-reg-text"
                  >
                    {generalStatus}
                    {generalStatusOverride}
                  </Typography>
                </Box>
              )}

              {user?.dasApp && (
                <Box
                  className="application-header-app-status-block-row-item"
                  sx={{ minWidth: 0, display: "block", p: 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800 }}
                    className="application-header-app-status-block-row-item-bold-text"
                  >
                    District/Authority Application Status:
                  </Typography>
                  <Typography
                    variant="body2"
                    className="application-header-app-status-block-row-item-reg-text"
                  >
                    {dasStatus} {dasStatusOverride}
                  </Typography>
                </Box>
              )}
            </Box>

            {isAdminView && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    width: "100%",
                    p: { xs: 1.1, sm: 1.25 },
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "warning.light",
                    background:
                      "linear-gradient(180deg, #FFF8E5 0%, #FFFDF6 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          display: "block",
                          fontWeight: 800,
                          letterSpacing: 0.6,
                          color: "warning.dark",
                          lineHeight: 1.1,
                          mb: 0.1,
                        }}
                      >
                        Admin Comments
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          fontStyle: "italic",
                          lineHeight: 1.2,
                        }}
                      >
                        Admin-only in admin view.
                      </Typography>
                    </Box>
                    {!isEditingComments && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsEditingComments(true)}
                        sx={{ flexShrink: 0, alignSelf: { xs: "flex-end", sm: "center" } }}
                      >
                        Edit Comments
                      </Button>
                    )}
                  </Box>
                  {isEditingComments ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        placeholder="Add internal admin notes for this student..."
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255,255,255,0.8)",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          mt: 1.25,
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="text"
                          onClick={() => {
                            setCommentDraft(adminComments);
                            setIsEditingComments(false);
                          }}
                          disabled={isSavingComments}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveComments}
                          disabled={
                            isSavingComments || commentDraft === adminComments
                          }
                        >
                          {isSavingComments ? (
                            <>
                              <CircularProgress
                                size={16}
                                color="inherit"
                                sx={{ mr: 1 }}
                              />
                              Saving...
                            </>
                          ) : (
                            "Save Comments"
                          )}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          color: "text.primary",
                          lineHeight: 1.45,
                        }}
                      >
                        {adminComments ||
                          "No admin comments on file for this student."}
                      </Typography>
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default memo(ApplicationsHeader);
